import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signInAnonymously,
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';

const AuthContext = createContext({});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      // Note: Cross-Origin-Opener-Policy warnings in console are harmless
      // They occur because Google's OAuth popup tries to check window.closed
      // but browser security policies block it. Authentication still works correctly.
    } catch (error) {
      // Filter out COOP-related warnings (they're not real errors)
      if (error.code !== 'auth/popup-closed-by-user' && 
          !error.message?.includes('Cross-Origin-Opener-Policy')) {
        console.error('Error signing in with Google:', error);
      }
      throw error;
    }
  };

  const signInAnonymouslyUser = async () => {
    try {
      await signInAnonymously(auth);
    } catch (error) {
      console.error('Error signing in anonymously:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // If no user is logged in, sign in anonymously
      if (!user) {
        try {
          await signInAnonymously(auth);
          // The auth state will change again after anonymous sign-in
          // so we'll get another callback with the user
        } catch (error) {
          console.error('Error signing in anonymously:', error);
          setLoading(false);
          // Continue even if anonymous sign-in fails
        }
      } else {
        setCurrentUser(user);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signInWithGoogle,
    signInAnonymously: signInAnonymouslyUser,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

